export default {
  id: 'position-sizing',
  title: 'Position Sizing in TradeLab',
  content: `## Position Sizing in TradeLab

Position sizing answers: **how many shares should I buy?**

The answer must always come from your risk tolerance — not from how confident you feel about the trade.

---

## The Formula

**Risk Amount = Account Size × Risk Per Trade %**
**Position Size = Risk Amount ÷ (Entry Price − Stop Loss Price)**

---

## Example

> Account Size: ₹10,00,000
> Risk Per Trade: 1% → Risk Amount = ₹10,000
> Entry Price: ₹500 | Stop Loss: ₹490 → Risk per share = ₹10
> Position Size = ₹10,000 ÷ ₹10 = **1,000 shares**
> Total Trade Value = 1,000 × ₹500 = ₹5,00,000

---

## How Stop Distance Affects Position Size

A tighter stop loss requires more shares to hit the same risk amount. A wider stop requires fewer shares. Both can have the same total risk — only the share count changes.

| Entry | Stop | Risk / Share | Shares (₹10,000 risk) | Trade Value |
|---|---|---|---|---|
| ₹500 | ₹498 | ₹2 | 5,000 | ₹25,00,000 |
| ₹500 | ₹495 | ₹5 | 2,000 | ₹10,00,000 |
| ₹500 | ₹490 | ₹10 | 1,000 | ₹5,00,000 |
| ₹500 | ₹480 | ₹20 | 500 | ₹2,50,000 |

> A stop that is too tight forces you into a very large position. If that stop is hit by normal daily price noise — not by the trade being wrong — you lose your full risk amount for no reason.

---

## Why Not Just Buy a Round Number?

If you buy 500 shares "because it feels right":
- Your actual risk might be ₹5,000 — half what you intended
- Or ₹25,000 — 2.5× your limit

Inconsistent sizing makes your results unpredictable. One oversized losing trade can wipe out multiple weeks of disciplined smaller gains.

---

## Position Sizing and ATR

A useful rule of thumb: your stop loss should be at least **1× ATR** away from your entry to avoid being stopped out by normal noise.

If ATR for a stock is ₹15, a stop only ₹5 away is likely to be hit even on a good trade. Set the stop at ₹15–20 away, then calculate position size from that.

---

## ACT Practice Sizing

In the ACT module, position size suggestions are shown based on a **fixed ₹25,000 base** per scenario. This gives you a consistent reference across all practice sessions regardless of stock price or stop distance, so you can compare quality across scenarios.

---

## How TradeLab Uses This

- ACT scoring checks whether your entry/stop implies a risk amount within your stated Risk Per Trade limit
- Plans that significantly overshoot your risk limit are penalised in the **Risk Management** scoring dimension
- The ACT trade plan form shows three size options at different stop distances so you can see the tradeoff before committing`,
};
